import { useTranslation } from '@pancakeswap/localization'
import { TradeType } from '@pancakeswap/sdk'
import { Button, Text, useModal, Box, Message, MessageText, AutoColumn, Dots, useToast, useOnClickOutside, FlexGap, Flex, ChevronDownIcon, CheckmarkIcon, ChevronUpIcon } from '@pancakeswap/uikit'
import { confirmPriceImpactWithoutFee } from '@pancakeswap/widgets-internal'
import { useCallback, useEffect, useState, useMemo, memo } from 'react'
import { SMART_ROUTER_ADDRESSES, SmartRouterTrade } from '@pancakeswap/smart-router/evm'
import { logGTMClickSwapEvent } from 'utils/customGTMEventTracking'

import { useIsTransactionUnsupported } from 'hooks/Trades'
import { GreyCard } from 'components/Card'
import { CommitButton } from 'components/CommitButton'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { AutoRow } from 'components/Layout/Row'
import SettingsModal, {
  RoutingSettingsButton,
  withCustomOnDismiss,
} from 'components/EMenu/GlobalSettings/SettingsModal'
import { SettingsMode } from 'components/EMenu/GlobalSettings/types'
import {
  BIG_INT_ZERO,
  PRICE_IMPACT_WITHOUT_FEE_CONFIRM_MIN,
  ALLOWED_PRICE_IMPACT_HIGH,
} from 'config/constants/exchange'
import { ApprovalState, useApproveCallback } from 'hooks/useApproveCallback'
import { Field } from 'state/swap/actions'
import { useExpertMode, useUserApproveType } from '@pancakeswap/utils/user'
import { warningSeverity } from 'utils/exchange'
import { useSwapState } from 'state/swap/hooks'
import { useCurrency } from 'hooks/Tokens'
import useWrapCallback, { WrapType } from 'hooks/useWrapCallback'
import { useCurrencyBalances } from 'state/wallet/hooks'
import { useSwapActionHandlers } from 'state/swap/useSwapActionHandlers'
import useTransactionDeadline from 'hooks/useTransactionDeadline'
import { useRoutingSettingChanged } from 'state/user/smartRouter'

import { useAccount } from 'wagmi'
import { useActiveChainId } from 'hooks/useActiveChainId'
import { useConfirmModalState } from 'views/Swap/V3Swap/hooks/useConfirmModalState'
import styled from 'styled-components'
import TransactionConfirmationModal from 'components/TransactionConfirmationModal'
import { formatNumber } from '@pancakeswap/utils/formatBalance'
import { useSlippageAdjustedAmounts, useSwapInputError, useParsedAmounts, useSwapCallback } from '../hooks'
import { computeTradePriceBreakdown } from '../utils/exchange'
import { ConfirmSwapModal } from './ConfirmSwapModal'
import { useWallchainApi } from '../hooks/useWallchain'
import { TransactionRejectedError } from '../hooks/useSendSwapTransaction'
import { ConfirmModalState } from '../types'


const SettingsModalWithCustomOnDismiss = withCustomOnDismiss(SettingsModal)

interface SwapCommitButtonPropsType {
  trade?: SmartRouterTrade<TradeType>
  tradeError?: Error
  tradeLoading?: boolean
  onDone?: () => void
}

const ButtonWrapper = styled.div`
	position: relative;
`

const ApproveButton = styled(Button)`
	padding: 0;
	border-radius: 12px;
	font-weight: bold;
	display: flex;
	align-items: stretch;
  	height: 72px;
	width: 100%;
`

const ApproveDropDown = styled(Box)`
    background: #332D3E;
    border-radius: 16px;
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    padding: 16px;
    z-index: 1;
		& > div {
			padding: 16px;
			cursor: pointer;
			&:hover {
				background: #3A3447;
				border-radius: 8px;
			}
		}
`

const CheckIcon = styled(CheckmarkIcon).attrs({ width: "18px" })<{ checked: boolean }>`
  stroke: ${({ checked, theme }) => checked ? theme.colors.primary : "transparent"};
  fill: ${({ checked, theme }) => checked ? theme.colors.primary : "transparent"};
  border: 2px solid ${({ checked, theme }) => checked ? theme.colors.primary : theme.colors.backgroundDisabled};
  border-radius: 4px;
`

const ApproveButtonBox = ({ onClickItem, amount, symbol }: { onClickItem: (index: string) => void, amount: string, symbol: string }) => {
	const { t } = useTranslation()
  const [approveType, setApproveType] = useUserApproveType()
	const [showDropDown, setDropDown] = useState(false)
	const [targetRef, setTargetRef] = useState<HTMLDivElement | null>(null)
	useOnClickOutside(
		targetRef,
		useCallback(() => {
			setDropDown(false);
		}, [setDropDown])
	)
	return <ButtonWrapper ref={setTargetRef}>
		<ApproveButton>
      <Flex alignItems="center" justifyContent="center" flex={1} onClick={() => onClickItem(approveType)}>
        <Text fontSize={20}>Approve</Text>
      </Flex>
			<Flex width="72px" borderLeft="1px solid #FFFFFF26" ml="auto" alignItems="center" justifyContent="center" onClick={() => setDropDown(!showDropDown)}>
        {
          showDropDown
				  ? <ChevronUpIcon width={24} />
				  : <ChevronDownIcon width={24} />
        }
			</Flex>
		</ApproveButton>
		{
			showDropDown &&
			<ApproveDropDown>
				<Box onClick={() => {
          setDropDown(false)
          setApproveType("one-time")
        }}>
          <FlexGap gap="8px" alignItems="center">
            <CheckIcon checked={approveType === "one-time"} />
            <Text fontSize={16}>{t("Approve one-time only")}</Text>
          </FlexGap>
          <Text fontSize={13} color="textDisabled" ml="26px" mt={1}>{t("You’ll give your approval to spend %amount% %symbol% on your behalf.", { amount, symbol })}</Text>
				</Box>
				<Box onClick={() => {
          setDropDown(false)
          setApproveType("unlimited")
        }}>
          <FlexGap gap="8px" alignItems="center">
            <CheckIcon checked={approveType === "unlimited"} />
            <Text fontSize={16}>{t("Approve unlimited amount")}</Text>
          </FlexGap>
          <Text fontSize={13} color="textDisabled" ml="26px" mt={1}>{t("You won’t need to approve again next time you want to spend %symbol%.", { symbol })}</Text>
				</Box>
			</ApproveDropDown>
		}
	</ButtonWrapper>
}

export const SwapCommitButton = memo(function _SwapCommitButton({
  trade,
  tradeError,
  tradeLoading,
  onDone,
}: SwapCommitButtonPropsType) {
  const { chainId } = useActiveChainId()
  const { t } = useTranslation()
  const { address: account } = useAccount()
  const [isExpertMode] = useExpertMode()
  const {
    typedValue,
    independentField,
    [Field.INPUT]: { currencyId: inputCurrencyId },
    [Field.OUTPUT]: { currencyId: outputCurrencyId },
  } = useSwapState()
  const inputCurrency = useCurrency(inputCurrencyId, true)
  const outputCurrency = useCurrency(outputCurrencyId, true)
  const swapIsUnsupported = useIsTransactionUnsupported(inputCurrency, outputCurrency)
  const { onUserInput } = useSwapActionHandlers()
  const {
    wrapType,
    execute: onWrap,
    inputError: wrapInputError,
  } = useWrapCallback(inputCurrency, outputCurrency, typedValue)
  const showWrap = wrapType !== WrapType.NOT_APPLICABLE
  const [isRoutingSettingChange, resetRoutingSetting] = useRoutingSettingChanged()
  const slippageAdjustedAmounts = useSlippageAdjustedAmounts(trade)

  const deadline = useTransactionDeadline()
  const [statusWallchain, approvalAddressForWallchain, wallchainMasterInput] = useWallchainApi(trade, deadline)
  const [wallchainSecondaryStatus, setWallchainSecondaryStatus] = useState<'found' | 'not-found'>('not-found')
  const routerAddress =
    statusWallchain === 'found' || wallchainSecondaryStatus === 'found'
      ? approvalAddressForWallchain
      : SMART_ROUTER_ADDRESSES[trade?.inputAmount?.currency?.chainId]
  const amountToApprove = slippageAdjustedAmounts[Field.INPUT]
  const relevantTokenBalances = useCurrencyBalances(account ?? undefined, [
    inputCurrency ?? undefined,
    outputCurrency ?? undefined,
  ])
  const currencyBalances = {
    [Field.INPUT]: relevantTokenBalances[0],
    [Field.OUTPUT]: relevantTokenBalances[1],
  }

  // check whether the user has approved the router on the input token
  const { approvalState, approveCallback, approveAmountCallback, revokeCallback, currentAllowance, isPendingError } = useApproveCallback(
    amountToApprove,
    routerAddress,
  )
  const [attemptingApprove, setAttemptingApprove] = useState(false)
  const { priceImpactWithoutFee } = useMemo(() => !showWrap && computeTradePriceBreakdown(trade), [showWrap, trade])
  const swapInputError = useSwapInputError(trade, currencyBalances)
  const parsedAmounts = useParsedAmounts(trade, currencyBalances, showWrap)
  const parsedIndepentFieldAmount = parsedAmounts[independentField]

  const onWallchainDrop = useCallback(() => {
    // setApprovalSubmitted(false)
  }, [])

  const {
    callback: swapCallback,
    error: swapCallbackError,
    reason: revertReason,
  } = useSwapCallback({
    trade,
    deadline,
    onWallchainDrop,
    wallchainMasterInput,
  })

  const [{ tradeToConfirm, swapErrorMessage, attemptingTxn, txHash }, setSwapState] = useState<{
    tradeToConfirm: SmartRouterTrade<TradeType> | undefined
    attemptingTxn: boolean
    swapErrorMessage: string | undefined
    txHash: string | undefined
  }>({
    tradeToConfirm: undefined,
    attemptingTxn: false,
    swapErrorMessage: undefined,
    txHash: undefined,
  })

  const handleSwap = useCallback(async () => {
    if (
      priceImpactWithoutFee &&
      !confirmPriceImpactWithoutFee(
        priceImpactWithoutFee,
        PRICE_IMPACT_WITHOUT_FEE_CONFIRM_MIN,
        ALLOWED_PRICE_IMPACT_HIGH,
        t,
      )
    ) {
      // setSwapState({ attemptingTxn: false, tradeToConfirm, swapErrorMessage: t("User rejected to confirm swap anyway"), txHash: undefined })
      return false
    }
    if (!swapCallback && !onWrap) {
      if (revertReason === 'insufficient allowance') {
        // setApprovalSubmitted(false)
        setWallchainSecondaryStatus('found')
        return false
      }
      return false
    }
    setSwapState({ attemptingTxn: true, tradeToConfirm, swapErrorMessage: undefined, txHash: undefined })
    try {
      const response = await (showWrap ? onWrap : swapCallback)()
      setWallchainSecondaryStatus('not-found')
      setSwapState({ attemptingTxn: false, tradeToConfirm, swapErrorMessage: undefined, txHash: response.hash })
      onUserInput(Field.INPUT, '')
      onDone?.()
    } catch (error) {
      setWallchainSecondaryStatus('not-found')
      const errorMessage = error instanceof Error ? error.message : t('An unknown error occurred. Please try again.')
      setSwapState({
        attemptingTxn: false,
        tradeToConfirm,
        swapErrorMessage: errorMessage,
        txHash: undefined,
      })
    }
    return true
  }, [priceImpactWithoutFee, t, swapCallback, onWrap, tradeToConfirm, revertReason, showWrap, onUserInput, onDone])
  // End Handlers

  // warnings on slippage
  const priceImpactSeverity = warningSeverity(priceImpactWithoutFee)

  const needApprove =
    !swapInputError &&
    (approvalState === ApprovalState.NOT_APPROVED || approvalState === ApprovalState.PENDING) &&
    inputCurrency.isToken
  
  const [onPresentConfirmModal] = useModal(
    <TransactionConfirmationModal 
      title="Swap"
      minWidth={['100%', null, '420px']}
      hash={txHash}
      attemptingTxn={attemptingTxn}
      content={() => null}
      pendingText={
        showWrap
        ? t("%action% %amount% PLS", { action: wrapType === WrapType.WRAP ? 'Wrap' : wrapType === WrapType.UNWRAP ? 'Unwrap' : null, amount: typedValue })
        : t("Swap %amount0% %symbol0% for %amount1% %symbol1%", { 
          amount0: Number(trade?.inputAmount?.toSignificant(6)), 
          symbol0: inputCurrency?.symbol, 
          amount1: Number(trade?.outputAmount?.toSignificant(6)), 
          symbol1: outputCurrency?.symbol, 
        })
      }
    />,
    false,
    true,
    'swap-confirm-modal'
  )

  const [onPresentApproveConfirmModal] = useModal(
    <TransactionConfirmationModal 
      title="Approve"
      minWidth={['100%', null, '420px']}
      hash={undefined}
      attemptingTxn={attemptingApprove}
      content={() => null}
      pendingText={t("Approve %amount% %symbol%", { amount: Number(trade?.inputAmount?.toSignificant(6)), symbol: inputCurrency?.symbol })}
    />,
    false,
    true,
    'approve-confirm-modal'
  )
  // End Modals

  useEffect(() => {
    if (attemptingTxn)
      onPresentConfirmModal()
  }, [attemptingTxn, onPresentConfirmModal])

  const handleApprove = useCallback(async (item) => {
    onPresentApproveConfirmModal()
    setAttemptingApprove(true)
    try {
      if (item === "one-time")
        await approveAmountCallback()
      else
        await approveCallback()
    } finally {
      setAttemptingApprove(false)
    }
  }, [approveAmountCallback, approveCallback, onPresentApproveConfirmModal])

  const onSwapHandler = useCallback(async () => {
    handleSwap()
    // logGTMClickSwapEvent()
  }, [handleSwap])

  if (swapIsUnsupported) {
    return (
      <Button width="100%" disabled>
        {t('Unsupported Asset')}
      </Button>
    )
  }

  if (!account) {
    return <ConnectWalletButton width="100%" style={{ fontSize: '20px', minHeight: '72px' }} />
  }

  if (showWrap) {
    return (
      <CommitButton width="100%" disabled={Boolean(wrapInputError) || attemptingTxn} style={{ fontSize: 20, minHeight: '72px' }} onClick={onSwapHandler}>
        {
          wrapInputError !== undefined
          ? wrapInputError
          : wrapType === WrapType.WRAP ? 'Wrap' : wrapType === WrapType.UNWRAP ? 'Unwrap' : null
        }
      </CommitButton>
    )
  }

  const noRoute = !(trade?.routes?.length > 0) || tradeError

  const userHasSpecifiedInputOutput = Boolean(
    inputCurrency && outputCurrency && parsedIndepentFieldAmount?.greaterThan(BIG_INT_ZERO),
  )

  if (noRoute && userHasSpecifiedInputOutput && !tradeLoading) {
    return (
      <AutoColumn gap="12px">
        <CommitButton style={{ fontSize: 20, color: 'white', minHeight: '72px' }} disabled variant="danger">
          {t('Insufficient liquidity for this trade.')}
        </CommitButton>
        {isRoutingSettingChange && (
          <Message variant="warning" icon={<></>}>
            <AutoColumn gap="8px">
              <MessageText>{t('Unable to establish trading route due to customized routing.')}</MessageText>
              <AutoRow gap="4px">
                <RoutingSettingsButton
                  buttonProps={{
                    scale: 'xs',
                    p: 0,
                  }}
                  showRedDot={false}
                >
                  {t('Check your settings')}
                </RoutingSettingsButton>
                <MessageText>{t('or')}</MessageText>
                <Button variant="text" scale="xs" p="0" onClick={resetRoutingSetting}>
                  {t('Reset to default')}
                </Button>
              </AutoRow>
            </AutoColumn>
          </Message>
        )}
      </AutoColumn>
    )
  }

  const isValid = !swapInputError && !tradeLoading

  if (needApprove) {
    if (approvalState === ApprovalState.PENDING || attemptingApprove)
      return <CommitButton style={{ fontSize: 20, color: 'white', minHeight: '72px' }} disabled variant="danger">
        <Dots>{t('Approve')}</Dots>
      </CommitButton>
    return <ApproveButtonBox onClickItem={handleApprove} amount={amountToApprove.toExact()} symbol={inputCurrency.symbol} />
  }

  return (
    <Box>
      <CommitButton
        id="swap-button"
        width="100%"
        style={{ fontSize: 20, color: 'white', minHeight: '72px' }}
        variant={isValid && priceImpactSeverity > 2 && !swapCallbackError ? 'danger' : 'primary'}
        disabled={
          !isValid || (priceImpactSeverity > 3 && !isExpertMode) || !!swapCallbackError || statusWallchain === 'pending'
        }
        onClick={onSwapHandler}
      >
        {swapInputError ||
          (tradeLoading && <Dots>{t('Searching For The Best Price')}</Dots>) ||
          (priceImpactSeverity > 3 && !isExpertMode
            ? t('Price Impact Too High')
            : priceImpactSeverity > 2
              ? t('Swap Anyway')
              : t('Swap'))}
      </CommitButton>
    </Box>
  )
})